import React, { useEffect, useMemo, useState } from 'react';
import axios from 'axios';
import { useNavigate } from 'react-router-dom';
import { Calendar as BigCalendar, dateFnsLocalizer, Views } from 'react-big-calendar';
import { format, parse, startOfWeek, getDay, addDays } from 'date-fns';
import { fr } from 'date-fns/locale';
import {
  Loader2, AlertCircle, ExternalLink, Users, User, Repeat, Layers,
  Calendar as CalendarIcon, Settings,
} from 'lucide-react';
import 'react-big-calendar/lib/css/react-big-calendar.css';
import './expert-calendar.css';
import EventDetailsModal from './EventDetailsModal';
import { parseUtc } from '../lib/calendarDate';
import { eventStyleFor, PUBLIC_LEGEND, makeCalendarToolbar, EVENT_COLORS } from './calendarTheme';
import ExpertInterestButton from './ExpertInterestButton';


const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const TYPE_META = {
  public_slot:    { label: 'Slot public',          icon: Repeat, tint: 'teal'   },
  public_event:   { label: 'Event public',         icon: Users,  tint: 'amber'  },
  public_series:  { label: 'Série récurrente',     icon: Layers, tint: 'cyan'   },
  // Brief LOT5 — sessions privées rendues en blocs "Occupé" anonymisés
  busy:           { label: 'Occupé',               icon: User,   tint: 'gray'   },
};

const locales = { fr };
const localizer = dateFnsLocalizer({
  format, parse,
  startOfWeek: () => startOfWeek(new Date(), { weekStartsOn: 1 }),
  getDay,
  locales,
});

const FR_MESSAGES = {
  date: 'Date', time: 'Heure', event: 'Événement',
  allDay: 'Toute la journée', week: 'Semaine', work_week: 'Semaine ouvrée',
  day: 'Jour', month: 'Mois', previous: '←', next: '→', yesterday: 'Hier',
  tomorrow: 'Demain', today: 'Aujourd\'hui', agenda: 'Agenda',
  noEventsInRange: 'Aucun événement public sur cette période.',
  showMore: (n) => `+${n} de plus`,
};

// Brief Jun 11 — toolbar custom : légende simplifiée (types publics uniquement).
const PublicCalendarToolbar = makeCalendarToolbar(PUBLIC_LEGEND);

const expandOccurrences = (event, windowStart, windowEnd) => {
  const occ = [];
  const baseStart = parseUtc(event.start_time);
  const baseEnd = parseUtc(event.end_time);
  const recPattern = event.recurrence?.pattern;
  const daysOfWeek = event.recurrence?.days_of_week || [];

  if (recPattern !== 'weekly' || daysOfWeek.length === 0) {
    occ.push({ start: baseStart, end: baseEnd, original: event });
    return occ;
  }

  const durMs = baseEnd - baseStart;
  let cursor = new Date(windowStart);
  cursor.setHours(baseStart.getHours(), baseStart.getMinutes(), 0, 0);
  while (cursor <= windowEnd) {
    const isoWeekday = (cursor.getDay() + 6) % 7;
    if (daysOfWeek.includes(isoWeekday) && cursor >= baseStart) {
      occ.push({
        start: new Date(cursor),
        end: new Date(cursor.getTime() + durMs),
        original: event,
      });
    }
    cursor = addDays(cursor, 1);
  }
  return occ;
};

/**
 * PublicExpertCalendar — sprint 1.0b-γ.
 *
 * Drop-in replacement for the legacy <CalendarView/> on Profile pages.
 * Reads the NEW unified model via /api/calendar/events/public/{user_id} (and
 * fuses public_slot events on the same exact start time).
 *
 * Modes:
 *   - Owner viewing their own profile: shows a "Aller dans Mes sessions →"
 *     CTA at the top (the private editing lives there now, sprint 1.0b-γ).
 *   - Visitor: shows a read-only calendar with click → details modal +
 *     "Réserver" CTA that takes them to /app/booking/{userId}.
 */
const PublicExpertCalendar = ({ userId, isOwnProfile, onBookRequest, embedded }) => {
  const navigate = useNavigate();
  const [events, setEvents] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [view, setView] = useState(Views.WEEK);
  const [date, setDate] = useState(new Date());
  const [details, setDetails] = useState(null);
  // Sprint 1.0c V1.b — F13: owner-only booked-day pastilles
  const [bookedDates, setBookedDates] = useState(new Set());

  useEffect(() => {
    let cancelled = false;
    const load = async () => {
      setLoading(true);
      setError('');
      try {
        const r = await axios.get(`${API}/calendar/events/public/${userId}`);
        if (!cancelled) setEvents(r.data.events || []);
      } catch (e) {
        if (!cancelled) setError('Impossible de charger le calendrier public.');
      } finally {
        if (!cancelled) setLoading(false);
      }
    };
    load();
    return () => { cancelled = true; };
  }, [userId]);

  // Sprint 1.0c V1.b — F13: pastilles "réservé" sur jours avec bookings (owner only)
  useEffect(() => {
    if (!isOwnProfile) return;
    let cancelled = false;
    (async () => {
      try {
        const r = await axios.get(`${API}/me/all-sessions`, { withCredentials: true });
        if (cancelled) return;
        const dates = new Set();
        for (const s of (r.data?.sessions || [])) {
          if (s.kind !== 'booking' || !['pending', 'confirmed', 'pending_payment'].includes(s.status)) continue;
          // Accept either ISO start_time or date+time
          let d;
          if (s.start_time && typeof s.start_time === 'string' && s.start_time.includes('T')) {
            d = new Date(s.start_time);
          } else if (s.date) {
            d = new Date(`${s.date}T${s.start_time || '00:00'}:00`);
          }
          if (d && !isNaN(d)) {
            const key = `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
            dates.add(key);
          }
        }
        setBookedDates(dates);
      } catch (e) { /* silent */ }
    })();
    return () => { cancelled = true; };
  }, [isOwnProfile, userId]);

  const windowStart = useMemo(() => addDays(date, -31), [date]);
  const windowEnd = useMemo(() => addDays(date, 62), [date]);

  // FUSED rendering — group public_slot events with same start_time into a single
  // bookable slot ("N sessions au choix"). Same logic as ExpertCalendar in β.
  const bigCalEvents = useMemo(() => {
    const out = [];
    const expanded = [];
    for (const e of events) {
      const occ = expandOccurrences(e, windowStart, windowEnd);
      for (const o of occ) expanded.push({ start: o.start, end: o.end, original: e });
    }
    const groups = new Map();
    for (const o of expanded) {
      if (o.original.type !== 'public_slot') {
        out.push({ title: o.original.title, start: o.start, end: o.end, resource: o.original });
        continue;
      }
      const key = `slot_${o.start.toISOString()}`;
      if (!groups.has(key)) groups.set(key, { start: o.start, end: o.end, options: [] });
      groups.get(key).options.push(o.original);
    }
    for (const g of groups.values()) {
      if (g.options.length === 1) {
        out.push({ title: g.options[0].title, start: g.start, end: g.end, resource: g.options[0] });
      } else {
        out.push({
          title: `${g.options.length} sessions au choix`,
          start: g.start, end: g.end,
          resource: { ...g.options[0], type: 'public_slot', _fused: true, _fused_options: g.options },
        });
      }
    }
    return out;
  }, [events, windowStart, windowEnd]);

  // Brief Jun 11 — système de couleurs unifié (cf. calendarTheme.js + légende)
  const eventStyleGetter = (event) => eventStyleFor(event.resource, 'auto');

  // Sprint 1.0c Bug K — index par jour : type prioritaire (public_event > slot)
  // pour afficher la bonne couleur de pastille en vue Mois.
  // Map : 'YYYY-MM-DD' → 'event' | 'slot' (priorité event si les 2 cohabitent)
  const dayTypeIndex = useMemo(() => {
    const idx = new Map();
    const now = new Date(); now.setHours(0, 0, 0, 0);
    for (const e of bigCalEvents) {
      if (!e?.start) continue;
      // On ignore les jours passés — la pastille indique une disponibilité actionnable.
      const day = new Date(e.start);
      day.setHours(0, 0, 0, 0);
      if (day < now) continue;
      const key = `${day.getFullYear()}-${String(day.getMonth() + 1).padStart(2, '0')}-${String(day.getDate()).padStart(2, '0')}`;
      const t = e.resource?.type;
      const cls = (t === 'public_event') ? 'event'
                : (t === 'public_slot' || t === 'public_series') ? 'slot'
                : null;
      if (!cls) continue;
      const existing = idx.get(key);
      // Priorité event > slot — un event public écrase un slot pour la pastille.
      if (existing === 'event') continue;
      idx.set(key, cls);
    }
    return idx;
  }, [bigCalEvents]);

  // Sprint 1.0c Bug K — count "N sessions disponibles ce mois" basé sur le
  // mois courant affiché par la grille (sync avec navigation).
  const sessionsThisMonth = useMemo(() => {
    const y = date.getFullYear();
    const m = date.getMonth();
    const now = new Date();
    return bigCalEvents.filter((e) => {
      if (!e?.start) return false;
      if (e.start < now) return false;
      const t = e.resource?.type;
      if (t !== 'public_slot' && t !== 'public_event' && t !== 'public_series') return false;
      return e.start.getFullYear() === y && e.start.getMonth() === m;
    }).length;
  }, [bigCalEvents, date]);

  const dayPropGetter = (d) => {
    const today = new Date(); today.setHours(0, 0, 0, 0);
    const cand = new Date(d); cand.setHours(0, 0, 0, 0);
    const classes = [];
    if (cand < today) classes.push('rbc-day-past');
    // Sprint 1.0c V1.b — F13: pastille "réservé" owner-only
    if (isOwnProfile) {
      const key = `${cand.getFullYear()}-${String(cand.getMonth() + 1).padStart(2, '0')}-${String(cand.getDate()).padStart(2, '0')}`;
      if (bookedDates.has(key)) classes.push('rbc-day-has-booking');
    }
    // Sprint 1.0c Bug K — pastille publique vue Mois (visiteur ET owner).
    const key = `${cand.getFullYear()}-${String(cand.getMonth() + 1).padStart(2, '0')}-${String(cand.getDate()).padStart(2, '0')}`;
    const publicKind = dayTypeIndex.get(key);
    if (publicKind === 'event') classes.push('rbc-day-has-public-event');
    else if (publicKind === 'slot') classes.push('rbc-day-has-public-slot');
    return classes.length ? { className: classes.join(' ') } : {};
  };

  // Sprint 1.0c V1.b — F13: "Prochains événements" list (top 5 upcoming)
  const upcomingList = useMemo(() => {
    const now = new Date();
    return bigCalEvents
      .filter(e => e.start >= now)
      .sort((a, b) => a.start - b.start)
      .slice(0, 5);
  }, [bigCalEvents]);

  return (
    <div className="space-y-3" data-testid="public-expert-calendar">
      {isOwnProfile && (
        <div className="flex items-center justify-between gap-3 p-3 rounded-xl bg-cyan-500/5 border border-cyan-400/20" data-testid="own-profile-calendar-cta">
          <div className="flex items-start gap-2 text-sm text-cyan-100/90">
            <Settings className="h-4 w-4 text-cyan-400 flex-shrink-0 mt-0.5" />
            <span>Vous voyez la vue publique. Pour créer ou modifier vos événements, rendez-vous dans <strong>Mes sessions</strong>.</span>
          </div>
          <button
            data-testid="goto-my-sessions-btn"
            onClick={() => navigate('/app/sessions')}
            className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-xs font-medium bg-gradient-to-r from-cyan-500 to-violet-500 text-white hover:opacity-90 whitespace-nowrap"
          >
            <CalendarIcon className="h-3.5 w-3.5" /> Mes sessions
          </button>
        </div>
      )}

      <div className="rounded-2xl bg-white dark:bg-[#0d1f35]/40 border border-gray-200 dark:border-white/10 backdrop-blur-md p-3 calendar-container-light-fix" data-testid="public-calendar-grid">
        {loading ? (
          <div className="h-[400px] flex items-center justify-center text-gray-500 dark:text-gray-400">
            <Loader2 className="h-6 w-6 animate-spin mr-2" /> Chargement du calendrier…
          </div>
        ) : error ? (
          <div className="h-[200px] flex items-center justify-center text-red-600 dark:text-red-300">
            <AlertCircle className="h-5 w-5 mr-2" /> {error}
          </div>
        ) : events.length === 0 ? (
          <div className="min-h-[200px] flex flex-col items-center justify-center gap-4 text-gray-500 dark:text-gray-400 text-sm py-6" data-testid="public-calendar-empty">
            <p>{isOwnProfile ? 'Aucun événement public pour le moment — crée ta première session dans Mes sessions.' : 'Aucun événement public pour le moment.'}</p>
            {!isOwnProfile && <ExpertInterestButton expertId={userId} />}
          </div>
        ) : (
          <div style={{ height: 680 }}>{/* Brief LOT5 — grille agrandie (540 → 680) */}
            <BigCalendar
              localizer={localizer}
              events={bigCalEvents}
              date={date}
              onNavigate={setDate}
              view={view}
              onView={setView}
              views={[Views.MONTH, Views.WEEK, Views.AGENDA]}
              defaultView={Views.WEEK}
              culture="fr"
              messages={FR_MESSAGES}
              eventPropGetter={eventStyleGetter}
              dayPropGetter={dayPropGetter}
              components={{ toolbar: PublicCalendarToolbar }}
              tooltipAccessor={(e) => `${TYPE_META[e.resource.type]?.label || 'Événement'} · ${e.title}`}
              popup
              startAccessor="start"
              endAccessor="end"
              scrollToTime={new Date(new Date().setHours(8, 0, 0, 0))}
              min={new Date(new Date().setHours(0, 0, 0, 0))}
              max={new Date(new Date().setHours(23, 59, 0, 0))}
              step={30}
              timeslots={2}
              onSelectEvent={(slot) => {
                // Brief LOT5 — les blocs "Occupé" ne sont pas cliquables.
                if (slot?.resource?.type === 'busy') return;
                setDetails(slot.resource);
              }}
            />
          </div>
        )}
      </div>

      {!isOwnProfile && !embedded && (
        <div className="flex flex-col items-center gap-1.5">
          {/* Sprint 1.0c Bug K — count contextuel "N sessions disponibles ce mois" */}
          {sessionsThisMonth > 0 && (
            <p className="text-xs text-gray-500 dark:text-gray-400" data-testid="public-calendar-month-count">
              <span className="font-semibold text-gray-700 dark:text-gray-200">{sessionsThisMonth}</span>{' '}
              session{sessionsThisMonth > 1 ? 's' : ''} disponible{sessionsThisMonth > 1 ? 's' : ''} ce mois
            </p>
          )}
          <button
            data-testid="public-calendar-book-btn"
            onClick={() => (onBookRequest ? onBookRequest() : navigate(`/app/profile/${userId}?book=1`))}
            className="inline-flex items-center gap-1.5 px-4 py-2 rounded-xl text-sm font-medium bg-gradient-to-r from-cyan-500 to-violet-500 text-white hover:opacity-90"
          >
            <ExternalLink className="h-4 w-4" /> Réserver une session
          </button>
          {/* Brief 27 oct E.3 — aucun créneau ne convient → signal d'intérêt */}
          {events.length > 0 && <ExpertInterestButton expertId={userId} variant="inline" />}
        </div>
      )}

      {/* Sprint 1.0c V1.b — F13: Prochains événements list */}
      {/* Sprint 1.0c Bug O — light/dark contrast (WCAG AA) — was unreadable on light bg */}
      {!loading && !error && upcomingList.length > 0 && (
        <div className="rounded-2xl bg-white dark:bg-[#0d1f35]/40 border border-gray-200 dark:border-white/10 backdrop-blur-md p-4" data-testid="upcoming-events-list">
          <h3 className="text-xs font-semibold uppercase tracking-wider text-gray-600 dark:text-cyan-300/80 mb-3 flex items-center gap-2">
            <CalendarIcon className="h-3.5 w-3.5" />
            Prochains événements ({upcomingList.length})
          </h3>
          <ul className="space-y-2">
            {upcomingList.map((e, idx) => {
              const meta = TYPE_META[e.resource.type] || {};
              const Icon = meta.icon || CalendarIcon;
              // Brief Jun 11 — pastilles alignées sur le code couleur unifié
              const dotColor = e.resource.type === 'public_event' ? EVENT_COLORS.publicEvent.base
                : e.resource.type === 'public_series' ? EVENT_COLORS.series.base
                : EVENT_COLORS.mine.base;
              return (
                <li
                  key={`${e.resource.event_id}_${e.start.toISOString()}`}
                  className="flex items-center gap-3 p-2.5 rounded-lg bg-gray-50 dark:bg-white/5 hover:bg-gray-100 dark:hover:bg-white/10 border border-gray-200 dark:border-transparent cursor-pointer transition-colors"
                  data-testid={`upcoming-event-${idx}`}
                  onClick={() => setDetails(e.resource)}
                >
                  <span className="w-2 h-2 rounded-full shrink-0" style={{ background: dotColor }} />
                  <Icon className="h-4 w-4 text-gray-500 dark:text-cyan-200/70 shrink-0" />
                  <div className="flex-1 min-w-0">
                    <p className="text-sm font-medium text-gray-900 dark:text-white truncate">{e.title}</p>
                    <p className="text-xs text-gray-600 dark:text-cyan-100/60">
                      {format(e.start, 'EEE d MMM', { locale: fr })} · {format(e.start, 'HH:mm', { locale: fr })}
                    </p>
                  </div>
                  {!isOwnProfile && e.resource.type !== 'public_event' && (
                    <button
                      onClick={(ev) => { ev.stopPropagation(); setDetails(e.resource); }}
                      className="text-xs px-2 py-1 rounded-md bg-gradient-to-r from-cyan-500 to-violet-500 text-white font-medium shrink-0"
                      data-testid={`upcoming-event-book-${idx}`}
                    >
                      Réserver
                    </button>
                  )}
                </li>
              );
            })}
          </ul>
        </div>
      )}

      <EventDetailsModal
        event={details}
        isOpen={!!details}
        onClose={() => setDetails(null)}
        onChanged={() => {/* read-only on public profile */}}
        ownerUserId={null}  // disables edit/cancel buttons on public view
      />
    </div>
  );
};

export default PublicExpertCalendar;
